<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import * as XLSX from 'xlsx'
import { del, get, post } from '@/api'
import { useConfirmDialog } from '@/composables/useConfirmDialog'
import { useSnackbar } from '@/composables/useSnackbar'
import { getMappingFileType, getMappingFileTypeOptions, MAPPING_FILE_TYPES } from '@/config/mappingFileConfig'
import { useClientStore } from '@/stores/client'

definePage({
  meta: {
    title: '매핑 입력',
    action: 'read',
    subject: 'DriverConfig',
  },
})

// 스토어
const clientStore = useClientStore()
const selectedClient = computed(() => clientStore.selectedClient)

// 인터페이스
interface DriverConfig {
  id: number
  download_driver: {
    id: number
    name: string
  }
}

interface SheetInfo {
  id: number
  name: string
  productCount: number
  driverConfigId: number | null
  stored: boolean
}

interface MappingData {
  id: number
  name: string
  productCount: number
  data: {
    client_item_code: string | null
    target_item_code: string | null
    config_id?: number
  }[]
}

// 상태
const file = ref<File | null>(null)
const fileInput = ref<File[]>([])
const mappingFileType = ref(MAPPING_FILE_TYPES[0].id)
const sheets = ref<SheetInfo[]>([])
const mappings = ref<MappingData[]>([])
const clientDriverConfigs = ref<DriverConfig[]>([])
const hasEmptyCode = ref(false)
const loading = ref(false)
const processingSheetId = ref<number | null>(null)

// Composables
const snackbar = useSnackbar()

interface ConfirmDialogData {
  configId: number
  sheetId: number
  mappingData: { client_item_code: string | null; target_item_code: string | null; config_id?: number }[]
}

const confirmDialog = useConfirmDialog<ConfirmDialogData>()

// 미리보기 탭
const previewTab = ref(0)

// 파일 타입 옵션 (설정 파일에서 가져옴)
const fileTypeOptions = getMappingFileTypeOptions()

// 파일 변경 감시
watch(fileInput, async newFiles => {
  if (newFiles && newFiles.length > 0) {
    file.value = newFiles[0]
    await parseFile(newFiles[0])
  }
  else {
    file.value = null
    resetMappings()
  }
})

// 파일 파싱
const parseFile = async (inputFile: File) => {
  loading.value = true
  resetMappings()
  resetSheets()

  const fileTypeConfig = getMappingFileType(mappingFileType.value)
  if (!fileTypeConfig) {
    showNotification('지원하지 않는 파일 형식입니다.', 'error')
    loading.value = false

    return
  }

  try {
    const arrayBuffer = await inputFile.arrayBuffer()
    const workbook = XLSX.read(arrayBuffer, { type: 'array' })

    workbook.SheetNames.forEach((sheetName, index) => {
      const worksheet = workbook.Sheets[sheetName]
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][]

      // 설정에 정의된 행 수만큼 스킵 (헤더 등)
      const dataRows = jsonData.slice(fileTypeConfig.skipRows)

      const productCount = dataRows.length

      sheets.value.push({
        id: index,
        name: sheetName,
        productCount,
        driverConfigId: null,
        stored: false,
      })

      const mappingData: MappingData = {
        id: index,
        name: sheetName,
        productCount,
        data: [],
      }

      dataRows.forEach((row: unknown[]) => {
        const cols = fileTypeConfig.columns
        const clientItemCode = row[cols.client_item_code]
        const targetItemCode = row[cols.target_item_code]

        if (clientItemCode && targetItemCode) {
          mappingData.data.push({
            client_item_code: String(clientItemCode),
            target_item_code: String(targetItemCode),
          })
        }
        else if (clientItemCode || targetItemCode) {
          mappingData.data.unshift({
            client_item_code: clientItemCode ? String(clientItemCode) : null,
            target_item_code: targetItemCode ? String(targetItemCode) : null,
          })
          hasEmptyCode.value = true
        }
      })

      mappings.value.push(mappingData)
    })
  }
  catch (error) {
    console.error('Failed to parse file:', error)
    snackbar.error('파일 파싱에 실패했습니다.')
  }
  finally {
    loading.value = false
  }
}

// 매핑 저장
const storeMappings = async (sheetId: number, configId: number | null) => {
  if (!configId || !selectedClient.value?.id)
    return

  const mappingData = mappings.value.find(m => m.id === sheetId)?.data || []
  const preparedMappingData = mappingData.map(m => ({
    ...m,
    config_id: configId,
  }))

  // 기존 매핑 확인
  try {
    const { success, data } = await get<{ total: number }>(
      `/review/clients/${selectedClient.value.id}/target-item-map`,
      { driver_config_id: configId, per_page: 1 },
    )

    if (success && data && data.total > 0) {
      const driverName = clientDriverConfigs.value.find(c => c.id === configId)?.download_driver.name || ''

      confirmDialog.open({
        message: `${driverName}로 이미 등록된 매핑 정보가 ${data.total}개 있습니다.`,
        data: {
          configId,
          sheetId,
          mappingData: preparedMappingData,
        },
      })

      return
    }

    await createMappings(configId, sheetId, preparedMappingData)
  }
  catch (error) {
    console.error('Failed to check existing mappings:', error)
    snackbar.error('매핑 확인에 실패했습니다.')
  }
}

// 기존 매핑 삭제 후 재등록
const deleteAndCreateMappings = async () => {
  const dialogData = confirmDialog.data.value
  confirmDialog.close()

  if (!selectedClient.value?.id || !dialogData)
    return

  processingSheetId.value = dialogData.sheetId
  try {
    await del(`/review/clients/${selectedClient.value.id}/target-item-map?driver_config_id=${dialogData.configId}`)
    await createMappings(dialogData.configId, dialogData.sheetId, dialogData.mappingData)
  }
  catch (error) {
    console.error('Failed to delete mappings:', error)
    snackbar.error('기존 매핑 삭제에 실패했습니다.')
  }
  finally {
    processingSheetId.value = null
  }
}

// 매핑 생성
const createMappings = async (
  configId: number,
  sheetId: number,
  mappingData: { client_item_code: string | null; target_item_code: string | null; config_id?: number }[],
) => {
  confirmDialog.close()

  if (!selectedClient.value?.id)
    return

  processingSheetId.value = sheetId
  try {
    const { success } = await post(
      `/review/clients/${selectedClient.value.id}/target-item-map/bulk`,
      { mappings: mappingData },
    )

    if (success) {
      snackbar.success('매핑 등록이 완료되었습니다.')

      const sheet = sheets.value.find(s => s.id === sheetId)
      if (sheet)
        sheet.stored = true
    }
    else {
      snackbar.error('매핑 등록에 실패했습니다.')
    }
  }
  catch (error) {
    console.error('Failed to create mappings:', error)
    snackbar.error('매핑 등록에 실패했습니다.')
  }
  finally {
    processingSheetId.value = null
  }
}

// 드라이버 설정 로드
const loadClientDriverConfigs = async () => {
  if (!selectedClient.value?.id) {
    clientDriverConfigs.value = []

    return
  }

  try {
    const { success, data } = await get<{ data: DriverConfig[] }>(
      `/review/clients/${selectedClient.value.id}/driver-configs`,
      { per_page: 100 },
    )

    if (success && data)
      clientDriverConfigs.value = data.data || []
  }
  catch (error) {
    console.error('Failed to load driver configs:', error)
  }
}

// 초기화
const resetMappings = () => {
  mappings.value = []
}

const resetSheets = () => {
  sheets.value = []
  hasEmptyCode.value = false
}

const clearFile = () => {
  fileInput.value = []
  file.value = null
  resetMappings()
  resetSheets()
}

// 고객사 변경 감시
watch(selectedClient, () => {
  loadClientDriverConfigs()
  clearFile()
}, { immediate: true })
</script>

<template>
  <VRow>
    <!-- 고객사 선택 및 파일 업로드 -->
    <VCol cols="12">
      <VCard>
        <VCardTitle>매핑 입력</VCardTitle>
        <VCardText>
          <VRow>
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                :model-value="selectedClient?.id"
                :items="clientStore.clients"
                item-title="name"
                item-value="id"
                label="고객사 선택"
                placeholder="고객사를 선택하세요"
                clearable
                @update:model-value="(id: number | null) => clientStore.selectClientById(id)"
              />
            </VCol>
            <VCol
              cols="12"
              md="2"
            >
              <VSelect
                v-model="mappingFileType"
                :items="fileTypeOptions"
                item-title="title"
                item-value="value"
                label="파일 타입"
                :disabled="!!file || !selectedClient"
              />
            </VCol>
            <VCol
              cols="12"
              md="5"
            >
              <VFileInput
                v-model="fileInput"
                label="Excel 파일 선택"
                placeholder="파일을 드래그하거나 클릭하세요"
                accept=".xlsx,.xls"
                prepend-icon="tabler-file-spreadsheet"
                :disabled="!selectedClient"
                :loading="loading"
              />
            </VCol>
            <VCol
              cols="12"
              md="1"
            >
              <VBtn
                color="error"
                variant="outlined"
                :disabled="!file"
                block
                @click="clearFile"
              >
                삭제
              </VBtn>
            </VCol>
          </VRow>

          <VAlert
            v-if="hasEmptyCode"
            type="warning"
            class="mt-4"
          >
            일부 행에 빈 코드가 있습니다. 미리보기에서 확인해주세요.
          </VAlert>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 시트별 채널 설정 -->
    <VCol
      v-if="file && sheets.length > 0"
      cols="12"
    >
      <VCard>
        <VCardTitle>시트별 채널 설정</VCardTitle>
        <VCardText>
          <VTable density="compact">
            <thead>
              <tr>
                <th>시트</th>
                <th>제품 수</th>
                <th style="width: 300px;">
                  채널
                </th>
                <th style="width: 120px;">
                  매핑 등록
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="sheet in sheets"
                :key="sheet.id"
              >
                <td>{{ sheet.name }}</td>
                <td>{{ sheet.productCount }}</td>
                <td>
                  <VSelect
                    v-model="sheet.driverConfigId"
                    :items="clientDriverConfigs"
                    :item-title="(item: DriverConfig) => item.download_driver.name"
                    item-value="id"
                    density="compact"
                    hide-details
                    :disabled="sheet.stored"
                    placeholder="채널 선택"
                  />
                </td>
                <td>
                  <VBtn
                    :color="sheet.stored ? 'success' : 'primary'"
                    variant="outlined"
                    size="small"
                    :disabled="sheet.stored || !sheet.driverConfigId"
                    :loading="processingSheetId === sheet.id"
                    @click="storeMappings(sheet.id, sheet.driverConfigId)"
                  >
                    {{ sheet.stored ? '완료' : '등록' }}
                  </VBtn>
                </td>
              </tr>
            </tbody>
          </VTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- Excel 미리보기 -->
    <VCol
      v-if="file && mappings.length > 0"
      cols="12"
    >
      <VCard>
        <VCardTitle>Excel 미리보기</VCardTitle>
        <VCardText>
          <VTabs v-model="previewTab">
            <VTab
              v-for="mapping in mappings"
              :key="mapping.id"
              :value="mapping.id"
            >
              {{ mapping.name }} ({{ mapping.productCount }})
            </VTab>
          </VTabs>

          <VWindow v-model="previewTab">
            <VWindowItem
              v-for="mapping in mappings"
              :key="mapping.id"
              :value="mapping.id"
            >
              <VTable
                density="compact"
                class="mt-4"
              >
                <thead>
                  <tr>
                    <th style="width: 60px;">
                      #
                    </th>
                    <th>외부채널 상품 코드</th>
                    <th>자사몰 상품 코드</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in mapping.data.slice(0, 100)"
                    :key="index"
                    :class="{ 'bg-warning-lighten': !item.client_item_code || !item.target_item_code }"
                  >
                    <td>{{ index + 1 }}</td>
                    <td>{{ item.target_item_code || '(빈 값)' }}</td>
                    <td>{{ item.client_item_code || '(빈 값)' }}</td>
                  </tr>
                </tbody>
              </VTable>
              <div
                v-if="mapping.data.length > 100"
                class="text-center pa-4 text-muted"
              >
                ... 외 {{ mapping.data.length - 100 }}개 항목
              </div>
            </VWindowItem>
          </VWindow>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 확인 다이얼로그 -->
    <VDialog
      v-model="confirmDialog.isOpen"
      max-width="500"
    >
      <VCard>
        <VCardTitle>매핑 등록 확인</VCardTitle>
        <VCardText>
          {{ confirmDialog.message }}
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="error"
            variant="outlined"
            @click="deleteAndCreateMappings"
          >
            기존 매핑 삭제 후 재등록
          </VBtn>
          <VBtn
            color="primary"
            @click="confirmDialog.data && createMappings(confirmDialog.data.configId, confirmDialog.data.sheetId, confirmDialog.data.mappingData)"
          >
            기존 매핑 유지 및 추가 등록
          </VBtn>
          <VBtn
            variant="outlined"
            @click="confirmDialog.close()"
          >
            취소
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar.isVisible"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      {{ snackbar.message }}
    </VSnackbar>
  </VRow>
</template>

<style scoped>
.bg-warning-lighten {
  background-color: rgba(255, 193, 7, 0.1);
}
</style>
